import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { SubsidyReportsState } from './subsidyReportsTypes';
import subsidyReportsApiService from 'src/api/services/susbsidyReportsApiService';
import { AppState } from 'src/store/store';
import { GetSubsidyReportsVM, Paginated, Pagination } from 'src/api/contracts';
import { SelectOption } from 'src/common/commonTypes';

const initialState: SubsidyReportsState = {
    status: 'initial',
    filter: {},
    items: [],
    total: 0,
    pagination: {
        pageNumber: 1,
        pageSize: 100
    },
    requestId: undefined
};

export const loadSubsidyReportsFirstPage = createAsyncThunk<Paginated<GetSubsidyReportsVM>, void, { state: AppState }>(
    'subsidy-reports/load-first-page',
    async (_, { getState }) => {
        const state = getState();
        const filter = selectRequestFilter(state);
        const pagination = selectSubsidyReportsPagination(state);
        return await subsidyReportsApiService.loadSubsidyReports({
            filter,
            pagination: {
                pageNumber: 1,
                pageSize: pagination.pageSize
            }
        });
    }
);

export const loadSubsidyReportsNextPage = createAsyncThunk<
    Paginated<GetSubsidyReportsVM>,
    Pagination,
    { state: AppState }
>('subsidy-reports/load-next-page', async (pagination, { getState }) => {
    const state = getState();
    const filter = selectRequestFilter(state);
    return await subsidyReportsApiService.loadSubsidyReports({
        filter,
        pagination
    });
});

const slice = createSlice({
    name: 'subsidy-reports',
    initialState,
    reducers: {
        reset: () => initialState,
        reportNumberFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.reportNumber = action.payload;
        },
        airlineFilterChanged: (state, action: PayloadAction<SelectOption[]>) => {
            state.filter.airline = action.payload;
        },
        yearFilterChanged: (state, action: PayloadAction<string>) => {
            state.filter.year = action.payload;
        },
        monthFilterChanged: (state, action: PayloadAction<SelectOption[]>) => {
            state.filter.month = action.payload;
        },
        statusFilterChanged: (state, action: PayloadAction<SelectOption[]>) => {
            state.filter.status = action.payload;
        }
    },
    extraReducers(builder) {
        builder
            .addCase(loadSubsidyReportsFirstPage.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.pagination.pageNumber = 1;
            })
            .addCase(loadSubsidyReportsFirstPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                }
            })
            .addCase(loadSubsidyReportsFirstPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = [];
                    state.total = 0;
                }
            })
            .addCase(loadSubsidyReportsNextPage.pending, (state, action) => {
                state.requestId = action.meta.requestId;
                state.status = 'loading';
                state.pagination = action.meta.arg;
            })
            .addCase(loadSubsidyReportsNextPage.fulfilled, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = action.payload.data;
                    state.total = action.payload.totalCount;
                    state.pagination.pageNumber = action.payload.pageNumber;
                }
            })
            .addCase(loadSubsidyReportsNextPage.rejected, (state, action) => {
                if (state.requestId === action.meta.requestId) {
                    state.status = 'idle';
                    state.items = [];
                    state.total = 0;
                    state.pagination.pageNumber = 1;
                }
            });
    }
});

export const {
    reportNumberFilterChanged,
    airlineFilterChanged,
    yearFilterChanged,
    monthFilterChanged,
    statusFilterChanged
} = slice.actions;

const selectSubsidyReports = (state: AppState) => state.subsidyReportList;

export const selectSubsidyReportsStatus = (state: AppState) => selectSubsidyReports(state).status;

export const selectSubsidyReportsItems = (state: AppState) => selectSubsidyReports(state).items;

export const selectSubsidyReportsFilter = (state: AppState) => selectSubsidyReports(state).filter;

const selectRequestFilter = (state: AppState) => {
    const filter = selectSubsidyReportsFilter(state);
    return {
        number: filter.reportNumber ? Number(filter.reportNumber) : undefined,
        iataCodes: filter.airline?.length ? filter.airline.map((a) => a.value) : undefined,
        months: filter.month?.length ? filter.month.map((m) => Number(m.value)) : undefined,
        year: filter.year ? Number(filter.year) : undefined,
        statusCodes: filter.status?.length ? filter.status.map((s) => s.value) : undefined
    };
};

export const selectSubsidyReportsTotal = (state: AppState) => selectSubsidyReports(state).total;

export const selectSubsidyReportsPagination = (state: AppState) => selectSubsidyReports(state).pagination;

export default slice.reducer;
